import React, { useState } from 'react';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = 'Ime je obvezno';
    }
    
    // Validate email
    if (!formData.email.trim()) {
      newErrors.email = 'E-pošta je obvezna';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Vnesite veljaven e-poštni naslov';
    }
    
    // Validate subject
    if (!formData.subject.trim()) {
      newErrors.subject = 'Zadeva je obvezna';
    }
    
    // Validate message
    if (!formData.message.trim()) {
      newErrors.message = 'Sporočilo je obvezno';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Sporočilo mora vsebovati vsaj 10 znakov';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      setSubmitStatus('success');
      
      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="bg-white rounded-lg shadow-md p-8">
      {submitStatus === 'success' ? (
        <div className="text-center p-6">
          <CheckCircle size={48} className="mx-auto mb-4 text-green-500" />
          <h3 className="text-2xl font-bold text-primary-700 mb-2">Sporočilo poslano!</h3>
          <p className="text-accent-700 mb-6">
            Hvala za vaše sporočilo. Odgovorili vam bomo v najkrajšem možnem času.
          </p>
          <button 
            onClick={() => setSubmitStatus(null)} 
            className="btn-primary"
          >
            Pošlji novo sporočilo
          </button>
        </div>
      ) : submitStatus === 'error' ? (
        <div className="text-center p-6">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
          <h3 className="text-2xl font-bold text-primary-700 mb-2">Napaka pri pošiljanju</h3>
          <p className="text-accent-700 mb-6">
            Prišlo je do napake pri pošiljanju sporočila. Prosimo, poskusite znova.
          </p>
          <button 
            onClick={() => setSubmitStatus(null)} 
            className="btn-primary"
          >
            Poskusi znova
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <h3 className="text-2xl font-bold text-primary-700 mb-6">Pišite nam</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name field */}
            <div className="form-group">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Ime in priimek <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-300 focus:border-primary-500 outline-none transition ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Vpišite vaše ime in priimek"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-500">{errors.name}</p>
              )}
            </div>
            
            {/* Email field */}
            <div className="form-group">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                E-pošta <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-300 focus:border-primary-500 outline-none transition ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Vpišite vaš e-poštni naslov"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-500">{errors.email}</p>
              )}
            </div>
          </div>
          
          {/* Subject field */}
          <div className="form-group mt-6">
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
              Zadeva <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-300 focus:border-primary-500 outline-none transition ${
                errors.subject ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Vpišite zadevo sporočila"
            />
            {errors.subject && (
              <p className="mt-1 text-sm text-red-500">{errors.subject}</p>
            )}
          </div>
          
          {/* Message field */}
          <div className="form-group mt-6">
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
              Sporočilo <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={5}
              className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-primary-300 focus:border-primary-500 outline-none transition ${
                errors.message ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Vpišite vaše sporočilo"
            />
            {errors.message && (
              <p className="mt-1 text-sm text-red-500">{errors.message}</p>
            )}
          </div>
          
          <div className="mt-8">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`btn-primary w-full md:w-auto flex items-center justify-center ${
                isSubmitting ? 'opacity-75 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Pošiljanje...
                </>
              ) : (
                <>
                  <Send size={18} className="mr-2" />
                  Pošlji sporočilo
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ContactForm;